import { Slider, Box } from '@mui/material';
import { abTestColors, brandColors } from 'colors';
import { CampaignChannels } from 'domains/campaigns/types';
import useAudienceCount, { UseAudienceCountWithChannelResponse } from 'hooks/queries/useAudienceCount';
import { CampaignStatus } from 'models/enums';

type Props = {
    segmentId: number;
    width?: string;
    splitPercentage?: 50 | 100; // New prop to determine split mode
    campaignStatus?: CampaignStatus; // New prop for campaign status
};

const ABTestSlider = ({ segmentId, width = '300px', splitPercentage = 100, campaignStatus }: Props) => {
    const { data: segmentCount, isSuccess: isSegmentCountSuccess } = useAudienceCount(
        segmentId,
        CampaignChannels.EMAIL,
        {
            enabled: Boolean(segmentId),
        }
    );
    const countData = segmentCount as UseAudienceCountWithChannelResponse;
    const segmentDetailCount = isSegmentCountSuccess ? Number(countData?.count) : 0;
    const halfCount = Math.floor(segmentDetailCount / 2);

    const is50PercentSplit = splitPercentage === 50;

    const get50PercentGradient = () => {
        if (campaignStatus === CampaignStatus.WINNER_SCHEDULED) {
            // 1st segment darker grey, 2nd segment lighter grey, 3rd segment (winner-send) yellow
            const darkerGrey = abTestColors.remaining; // #D9D9D9
            const lighterGrey = '#ECECEC';
            return `linear-gradient(
                to right,
                ${darkerGrey} 0%,
                ${darkerGrey} 25%,
                ${lighterGrey} 25%,
                ${lighterGrey} 50%,
                ${brandColors.cumin} 50%,
                ${brandColors.cumin} 100%
            )`;
        }
        if (campaignStatus === CampaignStatus.WINNER_SENT) {
            // 1st and 2nd segments same as current, 3rd segment same color as 1st segment
            return `linear-gradient(
                to right,
                ${abTestColors.emailA} 0%,
                ${abTestColors.emailA} 25%,
                ${abTestColors.emailB} 25%,
                ${abTestColors.emailB} 50%,
                ${abTestColors.emailA} 50%,
                ${abTestColors.emailA} 100%
            )`;
        }
        // Default 50% split colors
        return `linear-gradient(
            to right,
            ${abTestColors.emailA} 0%,
            ${abTestColors.emailA} 25%,
            ${abTestColors.emailB} 25%,
            ${abTestColors.emailB} 50%,
            ${abTestColors.remaining} 50%,
            ${abTestColors.remaining} 100%
        )`;
    };

    const gradientBackground = is50PercentSplit
        ? get50PercentGradient()
        : `linear-gradient(
            to right,
            ${abTestColors.emailA} 0%,
            ${abTestColors.emailA} 50%,
            ${abTestColors.emailB} 50%,
            ${abTestColors.emailB} 100%
        )`;
    return (
        <Box sx={{ position: 'relative', width }}>
            <Slider
                value={10} // Lock at end for consistent visual
                min={0}
                max={10}
                disabled
                sx={{
                    width: '100%',
                    height: '8px',

                    // Thumb styling (thin vertical bar)
                    '& .MuiSlider-thumb': {
                        backgroundColor: '#ffffff',
                        border: '2px solid #333333',
                        width: 4,
                        height: '16px',
                        borderRadius: '2px',
                        '&:before': { display: 'none' },
                        '&:hover, &.Mui-focusVisible': { boxShadow: 'none' },

                        // Only show the end marker's label
                        '&::after': {
                            content: `"${segmentDetailCount + ' contacts'}"`,
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: '6px',
                            fontSize: '12px',
                            color: '#333',
                            whiteSpace: 'nowrap',
                        },
                    },

                    // Full slider background split based on mode
                    '& .MuiSlider-rail': {
                        borderRadius: 0,
                        opacity: 1,
                        height: '12px',
                        background: gradientBackground,
                    },

                    // Hide the default track fill (we already control colors via rail)
                    '& .MuiSlider-track': {
                        background: 'transparent',
                        border: 'none',
                    },
                }}
            />

            {/* Middle marker for 50% split mode */}
            {is50PercentSplit && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '9px',
                        transform: 'translateX(-50%)',
                        width: 4,
                        height: '16px',
                        backgroundColor: '#000',
                        // border: '2px solid #333333',
                        borderRadius: '2px',
                        '&::after': {
                            content: `"${halfCount + ' contacts'}"`,
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: '6px',
                            fontSize: '12px',
                            color: '#333',
                            whiteSpace: 'nowrap',
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default ABTestSlider;
